import {
  FormControl,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  Button,
  Compliance,
  DirectionsCar,
  Groups,
  MobileMenu,
  Selector,
  Typography,
  VehicleCargo,
} from '@thingsw/pitta-design-system';
import {
  jwtAxiosInst,
  LightColors,
  MobileLang,
  SemanticDarkColors,
  sendMessageBack,
  Webviewer,
} from '@thingsw/pitta-modules';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import SurveyContent from '../../components/survey/SurveyContent';
import { useTranslation } from 'react-i18next';
import ArrowBack from '@material-ui/icons/ArrowBack';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Close from '@material-ui/icons/Close';
import SurveyCheckBox from '../../components/survey/SurveyCheckBox';
import { useHistory, useLocation } from 'react-router-dom';
import Stepper from '../../components/Stepper';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { THEME } from '../../features/Theme/slice';
import { RootState } from '../../features/store';
import { detect } from 'detect-browser';

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    borderRadius: 12,
    padding: '8px 24px',
  },
  placeholderTextColor: {
    color: (props: any) => props.colors.primary['2'],
  },
  selectPaper: {
    width: 490,
  },
  radioRoot: {
    gap: 12,
    flexWrap: 'wrap',
    width: '90%',
    marginLeft: 8,
  },
  checkboxRoot: {
    width: '50%',
    paddingTop: 16,
    [theme.breakpoints.down(Webviewer.mobile)]: {
      width: '100%',
    },
    [theme.breakpoints.up(Webviewer.mobile)]: {
      '&:nth-of-type(odd)': {
        borderRight: (props: any) => `1px solid ${props.colors.primary['6']}`, // 홀수 요소의 오른쪽 테두리
      },
    },
  },
  checkboxItem: {
    margin: '0 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    borderBottom: (props: any) => `1px solid ${props.colors.primary['6']}`,
    paddingBottom: 16,
    height: 180,
  },
  stepper: {
    [theme.breakpoints.down(Webviewer.mobile)]: {
      marginBottom: 24,
    },
    [theme.breakpoints.up(Webviewer.mobile)]: {
      marginBottom: 70,
    },
  },
  papaerClass: {
    maxHeight: '30vh',
    overflowY: 'auto',
    width: 458,
    overflowX: 'hidden',
    borderRadius: 8,
    border: (props: any) => `1px solid ${props.colors.primary['6']}`,
    maxWidth: 'calc(100vw - 32px)',
  },
  mobileMenuDiv: {
    padding: '6px 20px',
    '&:active': {
      backgroundColor: (props: any) => props.colors.primary['6'],
    },
  },
  radioButton: {
    backgroundColor: (props: any) =>
      props.color === 'dark'
        ? SemanticDarkColors.primary['1']
        : LightColors.primary['0'],
    '& span': {
      '&.Mui-checked': {
        color: (props: any) => props.colors.primary['1'],
      },
    },
  },
  icon: {
    '& path': {
      fill: (props: any) => props.colors.primary['1'],
    },
  },
  MobileMenuPaper: {
    borderTop: (props: any) =>
      props.color === 'dark'
        ? `1px solid ${props.colors.primary['6']}`
        : 'none',
    borderLeft: (props: any) =>
      props.color === 'dark'
        ? `1px solid ${props.colors.primary['6']}`
        : 'none',
    borderRight: (props: any) =>
      props.color === 'dark'
        ? `1px solid ${props.colors.primary['6']}`
        : 'none',
    outline: 'none !important',
  },
  disableBtn: {
    '&.Mui-disabled:hover': {
      opacity: 0.45,
      backgroundColor: (props: any) =>
        `${props.secondaryColors.primary['7']} !important`,
    },
    '&.MuiButton-containedPrimary:hover': {
      backgroundColor: (props: any) =>
        `${props.secondaryColors.primary['7']} !important`,
    },
  },
}));

interface SurveyData {
  list: string[];
  key: string;
  question: string;
  seq: number;
  type: string;
}

interface SurveyPanelProps {
  onSkip: () => void;
}

export const SurveyPanel = ({ onSkip }: SurveyPanelProps) => {
  const [step, setStep] = useState(0);
  const [industry, setIndustry] = useState('');
  const [employees, setEmployees] = useState('');
  const [vehicles, setVehicles] = useState('');
  const [reasonList, setReasonList] = useState<string[]>([]);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);

  const { colors, color, secondaryColors } = useSelector(
    (state: RootState) => state[THEME]
  );

  const classes = useStyles({ colors, color, secondaryColors });
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const theme = useTheme() as Theme;
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));

  const location = useLocation<{ fleetaPromo?: boolean }>();

  const browser = detect();

  const isApp = useMemo(() => {
    if (
      browser?.name === 'ios-webview' ||
      browser?.name === 'chromium-webview'
    ) {
      return true;
    } else {
      return false;
    }
  }, [browser?.name]);

  const { data, isLoading } = useQuery({
    queryKey: ['survey-data'],
    queryFn: async () => {
      const lang = i18n.language;
      const res = await jwtAxiosInst.get<{
        message: string;
        response: SurveyData[];
        resultCode: string;
      }>(`/survey/form?type=fleetaFirstLogin&lang=${MobileLang[lang]}`);

      return res.data;
    },
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const { mutate } = useMutation({
    mutationFn: async ({
      surveyData,
      answer,
    }: {
      surveyData: SurveyData;
      answer: number[];
    }) => {
      await jwtAxiosInst.post('/survey', {
        type: surveyData.type,
        key: surveyData.key,
        answer,
      });
    },
  });

  const postSurvey = useCallback(() => {
    if (data) {
      const answer = [];

      switch (step) {
        case 0:
          answer.push(Number(industry));
          break;
        case 1:
          answer.push(data.response[1].list.indexOf(employees));
          break;
        case 2:
          answer.push(data.response[2].list.indexOf(vehicles));
          break;
        case 3:
          reasonList.forEach((reason) => {
            answer.push(data.response[3].list.indexOf(reason));
          });
          break;
        default:
          break;
      }

      mutate({
        surveyData: data.response[step],
        answer,
      });
    }
  }, [data, employees, industry, mutate, reasonList, step, vehicles]);

  const handleNext = useCallback(() => {
    postSurvey();

    if (step < 3 && data) {
      setStep(step + 1);
      return;
    }

    if (!sendMessageBack()) {
      const fleetaPromo = location.state?.fleetaPromo;
      history.push('/cameras', { fleetaPromo });
    }
  }, [data, history, location.state?.fleetaPromo, postSurvey, step]);

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      return;
    }
  };

  const isDisabledNext = useMemo(() => {
    switch (step) {
      case 0:
        return industry === '';
      case 1:
        return employees === '';
      case 2:
        return vehicles === '';
      case 3:
        return reasonList.length === 0;
      default:
        return false;
    }
  }, [employees, industry, reasonList.length, step, vehicles]);

  const contentMarkup = useMemo(() => {
    let question = '';

    switch (step) {
      case 0:
        const answerList = [
          {
            key: '',
            value: '',
          },
        ];

        if (data?.response[0]) {
          question = data?.response[0].question;

          data.response[0].list.forEach((item, index) => {
            answerList.push({
              key: index.toString(),
              value: item,
            });
          });
        }

        return (
          <>
            <Typography
              style={{
                marginBottom: 24,
              }}
              htmlColor={colors.primary['1']}
              variant='H4'
            >
              {question}
            </Typography>
            <div
              style={{
                position: 'relative',
              }}
            >
              {isApp ? (
                <div>
                  <div
                    style={{
                      width: '100%',
                      height: 44,
                      borderRadius: 8,
                      border: `1px solid ${colors.primary['4']}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0 16px',
                      opacity: industry === '' ? 0.5 : 1,
                    }}
                    onClick={() => setOpenMobileMenu(true)}
                  >
                    <Typography htmlColor={colors.primary['1']} variant='Body'>
                      {answerList.find((a) => a.key === industry)?.value ||
                        'Business industry'}
                    </Typography>
                    <ArrowDropDownIcon htmlColor={colors.primary['1']} />
                  </div>
                  <MobileMenu
                    paperClasses={classes.MobileMenuPaper}
                    open={openMobileMenu}
                    onClose={() => setOpenMobileMenu(false)}
                  >
                    {answerList.map((item) => (
                      <div
                        onClick={() => {
                          setIndustry(item.key);
                          setOpenMobileMenu(false);
                        }}
                        className={classes.mobileMenuDiv}
                      >
                        <Typography
                          htmlColor={colors.primary['1']}
                          variant='Body'
                          key={item.key}
                        >
                          {item.value}
                        </Typography>
                      </div>
                    ))}
                  </MobileMenu>
                </div>
              ) : (
                <Selector
                  onChange={(e) => {
                    setIndustry(e.key);
                  }}
                  keyValue={industry}
                  dense
                  paperClasses={{
                    root: classes.papaerClass,
                  }}
                  select={false}
                  placeholder={t('Business Industry')}
                  outlinedInputClassName={clsx(classes.select, {
                    [classes.placeholderTextColor]: industry === '',
                  })}
                  menuItemClassName={classes.selectPaper}
                  items={answerList}
                />
              )}
            </div>
          </>
        );

      case 1:
        const employeesItems: string[] = [];

        if (data?.response[1]) {
          question = data?.response[1].question;

          data.response[1].list.forEach((item) => {
            employeesItems.push(item);
          });
        }

        return (
          <>
            <Typography
              style={{
                marginBottom: 24,
              }}
              htmlColor={colors.primary['1']}
              variant='H4'
            >
              {question}
            </Typography>
            <FormControl
              style={{
                width: '100%',
              }}
            >
              <RadioGroup
                className={classes.radioRoot}
                name='employees'
                value={employees}
                onChange={(e) => {
                  setEmployees(e.target.value);
                }}
                row
              >
                {employeesItems.map((item) => (
                  <FormControlLabel
                    key={item}
                    value={item}
                    className={classes.radioButton}
                    control={
                      <Radio
                        style={{
                          color: employees !== item ? colors.primary['5'] : '',
                          marginInlineEnd: 8,
                          padding: 0,
                        }}
                        color='primary'
                      />
                    }
                    label={
                      <Typography variant='Button' style={{ lineHeight: 1 }}>
                        {item}
                      </Typography>
                    }
                    style={{
                      border: `1px solid ${
                        employees === item
                          ? colors.primary['1']
                          : colors.primary['4']
                      }`,
                      color:
                        employees === item
                          ? colors.primary['1']
                          : colors.primary['2'],
                      padding: '5px 16px',
                      borderRadius: 10,
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </>
        );
      case 2:
        const vehiclesItems: string[] = [];

        if (data?.response[2]) {
          question = data?.response[2].question;

          data.response[2].list.forEach((item) => {
            vehiclesItems.push(item);
          });
        }

        return (
          <>
            <Typography
              style={{
                marginBottom: 24,
              }}
              htmlColor={colors.primary['1']}
              variant='H4'
            >
              {question}
            </Typography>
            <FormControl>
              <RadioGroup
                className={classes.radioRoot}
                name='vehicles'
                value={vehicles}
                onChange={(e) => {
                  setVehicles(e.target.value);
                }}
                row
              >
                {vehiclesItems.map((item) => (
                  <FormControlLabel
                    key={item}
                    value={item}
                    className={classes.radioButton}
                    control={
                      <Radio
                        style={{
                          color: vehicles !== item ? colors.primary['5'] : '',
                          marginInlineEnd: 8,
                          padding: 0,
                        }}
                        color='primary'
                      />
                    }
                    label={
                      <Typography variant='Button' style={{ lineHeight: 1 }}>
                        {item}
                      </Typography>
                    }
                    style={{
                      border: `1px solid ${
                        vehicles === item
                          ? colors.primary['1']
                          : colors.primary['4']
                      }`,
                      color:
                        vehicles === item
                          ? colors.primary['1']
                          : colors.primary['2'],
                      padding: '5px 16px',
                      borderRadius: 10,
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </>
        );
      case 3:
        const drivingReasons: {
          key: string;
          value: string;
        }[] = [];

        const cargoReasons: {
          key: string;
          value: string;
        }[] = [];

        const driverReasons: {
          key: string;
          value: string;
        }[] = [];

        const complianceReasons: {
          key: string;
          value: string;
        }[] = [];

        if (data?.response[3]) {
          question = data?.response[3].question;

          data.response[3].list.forEach((item, index) => {
            if (index < 3) {
              drivingReasons.push({
                key: item,
                value: item,
              });
            }

            if (index >= 3 && index < 5) {
              cargoReasons.push({
                key: item,
                value: item,
              });
            }

            if (index >= 5 && index < 7) {
              driverReasons.push({
                key: item,
                value: item,
              });
            }

            if (index >= 7) {
              complianceReasons.push({
                key: item,
                value: item,
              });
            }
          });
        }

        return (
          <>
            <Typography
              style={{
                marginBottom: 24,
              }}
              htmlColor={colors.primary['1']}
              variant='H4'
            >
              {question}
            </Typography>
            <div
              style={{
                border: `1px solid ${colors.primary['6']}`,
                borderRadius: 16,
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                position: 'relative',
              }}
            >
              {/* Driving */}
              <div className={classes.checkboxRoot}>
                <div
                  style={{
                    margin: '0 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    borderBottom: `1px solid ${colors.primary['6']}`,
                    paddingBottom: 16,
                    height: mobile ? '100%' : 180,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      marginBottom: 4,
                    }}
                  >
                    <DirectionsCar className={classes.icon} />
                    <Typography htmlColor={colors.primary['1']} variant='H6'>
                      {t('Driving')}
                    </Typography>
                  </div>
                  {drivingReasons.map((item) => (
                    <SurveyCheckBox
                      item={item}
                      reasonList={reasonList}
                      setReasonList={setReasonList}
                      key={item.value}
                    />
                  ))}
                </div>
              </div>
              {/* Driver */}
              {mobile ? (
                <div className={classes.checkboxRoot}>
                  <div
                    style={{
                      margin: '0 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      paddingBottom: 16,
                      height: mobile ? '100%' : 172,
                      borderBottom: mobile
                        ? `1px solid ${colors.primary['6']}`
                        : 'none',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      <VehicleCargo className={classes.icon} />
                      <Typography htmlColor={colors.primary['1']} variant='H6'>
                        {t('Vehicle & Cargo')}
                      </Typography>
                    </div>
                    {cargoReasons.map((item) => (
                      <SurveyCheckBox
                        item={item}
                        reasonList={reasonList}
                        setReasonList={setReasonList}
                        key={item.value}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={classes.checkboxRoot}>
                  <div
                    style={{
                      margin: '0 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      borderBottom: `1px solid ${colors.primary['6']}`,
                      paddingBottom: 16,
                      height: mobile ? '100%' : 180,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      <Groups className={classes.icon} />
                      <Typography htmlColor={colors.primary['1']} variant='H6'>
                        {t('Driver')}
                      </Typography>
                    </div>
                    {driverReasons.map((item) => (
                      <SurveyCheckBox
                        item={item}
                        reasonList={reasonList}
                        setReasonList={setReasonList}
                        key={item.value}
                      />
                    ))}
                  </div>
                </div>
              )}
              {/* Cargo */}
              {mobile ? (
                <div className={classes.checkboxRoot}>
                  <div
                    style={{
                      margin: '0 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      borderBottom: `1px solid ${colors.primary['6']}`,
                      paddingBottom: 16,
                      height: mobile ? '100%' : 180,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      <Groups className={classes.icon} />
                      <Typography htmlColor={colors.primary['1']} variant='H6'>
                        {t('Driver')}
                      </Typography>
                    </div>
                    {driverReasons.map((item) => (
                      <SurveyCheckBox
                        item={item}
                        reasonList={reasonList}
                        setReasonList={setReasonList}
                        key={item.value}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={classes.checkboxRoot}>
                  <div
                    style={{
                      margin: '0 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      paddingBottom: 16,
                      height: mobile ? '100%' : 172,
                      borderBottom: mobile
                        ? `1px solid ${colors.primary['6']}`
                        : 'none',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      <VehicleCargo className={classes.icon} />
                      <Typography htmlColor={colors.primary['1']} variant='H6'>
                        {t('Vehicle & Cargo')}
                      </Typography>
                    </div>
                    {cargoReasons.map((item) => (
                      <SurveyCheckBox
                        item={item}
                        reasonList={reasonList}
                        setReasonList={setReasonList}
                        key={item.value}
                      />
                    ))}
                  </div>
                </div>
              )}
              {/* Compliance */}
              <div className={classes.checkboxRoot}>
                <div
                  style={{
                    margin: '0 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    paddingBottom: 16,
                    height: mobile ? '100%' : 172,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      marginBottom: 4,
                    }}
                  >
                    <Compliance className={classes.icon} />
                    <Typography htmlColor={colors.primary['1']} variant='H6'>
                      {t('Compliance')}
                    </Typography>
                  </div>
                  {complianceReasons.map((item) => (
                    <SurveyCheckBox
                      item={item}
                      reasonList={reasonList}
                      setReasonList={setReasonList}
                      key={item.value}
                    />
                  ))}
                </div>
              </div>
            </div>
          </>
        );
      default:
        return <></>;
    }
  }, [
    step,
    data?.response,
    colors.primary,
    isApp,
    industry,
    classes.MobileMenuPaper,
    classes.papaerClass,
    classes.select,
    classes.placeholderTextColor,
    classes.selectPaper,
    classes.radioRoot,
    classes.checkboxRoot,
    classes.icon,
    classes.mobileMenuDiv,
    classes.radioButton,
    openMobileMenu,
    t,
    employees,
    vehicles,
    mobile,
    reasonList,
  ]);

  return (
    <>
      {isApp && (
        <header
          style={{
            width: '100%',
            height: 48,
            display: 'flex',
            alignItems: 'center',
            padding: 9,
          }}
        >
          <Close
            htmlColor={colors.primary['1']}
            onClick={() => {
              onSkip();
            }}
          />
        </header>
      )}
      <main
        style={{
          overflowY: 'auto',
          maxWidth: 490,
          margin: '0 auto',
          marginTop: isApp ? 16 : 54,
          minHeight: mobile ? 'calc(100vh - 110px)' : '',
          padding: '0 16px 80px 16px',
          position: 'relative',
          height: 'calc(100vh - 56px)',
        }}
      >
        <Stepper currentStep={step} stepCount={4} className={classes.stepper} />

        {/* // Survey content */}
        <SurveyContent
          title={
            step === 0
              ? isApp
                ? t('Welcome to FLEETA')
                : t('Welcome to FLEETA Webviewer.')
              : t('Let FLEETA get_')
          }
          description={t('Help improve your_')}
          content={contentMarkup}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            position: mobile && step !== 3 ? 'absolute' : 'relative',
            bottom: mobile && step !== 3 ? 24 : 0,
            right: mobile && step !== 3 ? 16 : 0,
            width: mobile && step !== 3 ? 'calc(100vw - 32px)' : '100%',
          }}
        >
          <div>
            {step !== 0 && !isApp && (
              <Button
                variant='text'
                style={{
                  backgroundColor: 'transparent',
                  color: colors.primary['2'],
                }}
                startIcon={<ArrowBack />}
                onClick={handleBack}
              >
                {t('Back')}
              </Button>
            )}
          </div>

          {/* App Button */}
          {isApp && (
            <div
              style={{
                display: 'flex',
                width: '100%',
                gap: 16,
              }}
            >
              {step !== 0 && (
                <Button
                  variant='outlined'
                  style={{
                    width: '100%',
                    backgroundColor:
                      color === 'dark'
                        ? SemanticDarkColors.primary['2']
                        : colors.primary['0'],
                    color: colors.primary['1'],
                    border: `1px solid ${colors.primary['4']}`,
                  }}
                  onClick={handleBack}
                >
                  {t('Back')}
                </Button>
              )}
              <Button
                className={classes.disableBtn}
                disabled={isDisabledNext}
                color='primary'
                style={{
                  width: '100%',
                  height: 48,
                  color: LightColors.primary['0'],
                }}
                onClick={handleNext}
              >
                {step !== 3 ? t('Next') : t('Done')}
              </Button>
            </div>
          )}

          {/* Web Button */}
          {!isApp && (
            <div>
              <Button
                variant='text'
                style={{
                  backgroundColor: 'transparent',
                  color: colors.primary['2'],
                }}
                onClick={() => onSkip()}
              >
                {t('Skip')}
              </Button>
              <Button
                className={classes.disableBtn}
                color='primary'
                disabled={isDisabledNext}
                onClick={handleNext}
                loading={isLoading}
              >
                <Typography htmlColor={colors.primary['0']} variant='Button'>
                  {step !== 3 ? t('Next') : t('Done')}
                </Typography>
              </Button>
            </div>
          )}
        </div>
      </main>
    </>
  );
};
